import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Grid from "@mui/material/Grid";
import { MCard } from "components/Shared";
import Summary from "components/EmployeeTimeLogs/FieldRepTimeLogs/Summary";
import Performance from "components/EmployeeTimeLogs/FieldRepTimeLogs/Performance";
import CaseMileage from "components/EmployeeTimeLogs/FieldRepTimeLogs/CaseMileage.tsx";
import MacCard from "components/User/Mac/MacCard";
import FieldRepContactInfo from "components/EmployeeTimeLogs/FieldRepTimeLogs/FieldRepContactInfo";
import { getPaySummary } from "actions/homeowner/fieldReps";
import { clone } from "lodash";
import { dateToStringYy, dateToStringWithDOW } from "components/Shared/Formatters";
import AdminTimeLog from "components/Shared/AdminTimeLog";
import { ALLOWEDITDATEFORFRTIMELOG, EMPLOYEENOTES_VIEW } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import EmployeeTEAM from "components/Employee/EmployeeTEAM";
import CaseProfile from "containers/Homeowner/CaseProfile/CaseProfile";
import { filterOnFilterDate } from "components/Homeowner/History/DataFormatters";

const FieldRepTimeLogDetails = ({
  selectedPayPeriod,
  selectedEmployee,
  getPaySummary,
  isFetchingInspectorPayPeriodSummary,
  inspectorPayPeriodSummary,
  canEditAdminTimeLog,
  canViewNotes,
}) => {
  const [filterDate, setFilterDate] = useState();
  const [selectedControlNumber, setSelectedControlNumber] = useState(null);

  useEffect(() => {
    setFilterDate(undefined);
  }, [selectedEmployee.number, selectedPayPeriod.id]);

  const getRows = (paySummary) => {
    const { payPeriodCases, totalMac, totalCaseCost, totalMileage, totalHours } = paySummary;
    let rows = clone(payPeriodCases).filter((row) => filterOnFilterDate(filterDate, row.submittedDate));
    rows.map((row) => (row.submittedDate = dateToStringYy(row.submittedDate)));
    if (rows.length > 0) {
      rows.push({
        submittedDate: "Total",
        mileage: totalMileage,
        minutes: totalHours,
        mac: `$${totalMac}`,
        caseCost: `$${totalCaseCost}`,
      });
    }
    return rows;
  };

  const onControlNumberSelected = (controlNumber) => {
    if (controlNumber !== selectedControlNumber) setSelectedControlNumber(controlNumber);
  };

  const onFilterDateChange = (date) => setFilterDate(date && new Date(date));

  return (
    <Grid size={12} container gap={1}>
      {filterDate && (
        <Grid size={12}>
          <MCard
            title={`Cards are currently filtered to ${dateToStringWithDOW(filterDate)}`}
            actions={[{ icon: "close", helpText: "Clear Filter", onClick: () => setFilterDate(null) }]}
          />
        </Grid>
      )}
      <Grid size={12}>
        <FieldRepContactInfo inspectorNumber={selectedEmployee.number} />
      </Grid>
      <Grid size={12}>
        <Summary
          employeeName={selectedEmployee.name}
          inspectorNumber={selectedEmployee.number}
          payPeriodId={selectedPayPeriod.id}
          filterDateCallBack={onFilterDateChange}
        />
      </Grid>
      <Grid size={12}>
        <Performance
          inspectorNumber={selectedEmployee.number}
          payPeriodId={selectedPayPeriod.id}
          payPeriodClosed={selectedPayPeriod.closedDate}
          onControlNumberSelected={onControlNumberSelected}
          filterDate={filterDate}
        />
      </Grid>
      <Grid size={12}>
        <CaseMileage
          inspectorNumber={selectedEmployee.number}
          payPeriodId={selectedPayPeriod.id}
          payPeriodClosed={selectedPayPeriod.closedDate}
          onControlNumberSelected={onControlNumberSelected}
          filterDate={filterDate}
        />
      </Grid>
      <Grid size={12}>
        <MacCard
          // Key this element so it reloads when its data changes since it wasnt built with prop changing in mind
          key={`MacCard-${selectedEmployee.number}-${selectedPayPeriod.id}`}
          payPeriod={selectedPayPeriod}
          hidePayPeriodSelection
          employeeID={selectedEmployee.number}
          getPaySummary={getPaySummary}
          isFetchingPayPeriodSummary={isFetchingInspectorPayPeriodSummary}
          paySummary={inspectorPayPeriodSummary}
          getRows={getRows}
          FRMac
        />
      </Grid>
      <Grid size={12}>
        <AdminTimeLog
          // Key this element so it reloads when its data changes
          key={`AdminTimeLog-${selectedEmployee.number}-${selectedPayPeriod.id}`}
          inspectorNumber={selectedEmployee.number}
          payPeriodId={selectedPayPeriod.id}
          canAddAndEdit={canEditAdminTimeLog}
          filterDate={filterDate}
        />
      </Grid>
      {canViewNotes && (
        <Grid size={12}>
          <EmployeeTEAM
            key={`EmployeeTeam-${selectedEmployee.number}}`}
            internetUserId={selectedEmployee.internetUserId}
            overrideCellWidth
            filterDate={filterDate}
          />
        </Grid>
      )}
      {selectedControlNumber && (
        <Grid size={12}>
          <CaseProfile controlNumber={selectedControlNumber} verticalDisplay={true} />
        </Grid>
      )}
    </Grid>
  );
};

FieldRepTimeLogDetails.propTypes = {
  selectedPayPeriod: PropTypes.object,
  selectedEmployee: PropTypes.object,
  // Dispatch and State props
  getPaySummary: PropTypes.func,
  isFetchingInspectorPayPeriodSummary: PropTypes.bool,
  inspectorPayPeriodSummary: PropTypes.array,
  canEditAdminTimeLog: PropTypes.bool,
  canViewNotes: PropTypes.bool,
};

const mapStateToProps = (state) => {
  return {
    isFetchingInspectorPayPeriodSummary: state.assignment.isFetchingInspectorPayPeriodSummary,
    inspectorPayPeriodSummary: state.assignment.inspectorPayPeriodSummary,
    canEditAdminTimeLog: userHasRight(ALLOWEDITDATEFORFRTIMELOG)(state),
    canViewNotes: userHasRight(EMPLOYEENOTES_VIEW)(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  getPaySummary(inspectorNumber, payPeriodId) {
    dispatch(getPaySummary(inspectorNumber, payPeriodId));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(FieldRepTimeLogDetails);
